import React, { useCallback, useEffect, useRef, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import api from "../../../services/api";
import { EmptyState, Pagination, errorText, formatDateTime, timeAgo } from "./common";

const ACTION_LABELS = {
  "user.create": "Created user",
  "user.update": "Updated user",
  "user.delete": "Deleted user",
  "user.password": "Set password",
  "user.verify": "Verified user",
  "user.suspend": "Suspended user",
  "quiz.delete": "Deleted quiz",
  "announcement.create": "Posted announcement",
  "announcement.update": "Edited announcement",
  "announcement.delete": "Deleted announcement",
  "message.update": "Updated message",
  "message.handle": "Handled message",
  "message.delete": "Deleted message",
};

const actionLabel = (code) => {
  if (!code) return "Action";
  if (ACTION_LABELS[code]) return ACTION_LABELS[code];
  const [thing, verb] = String(code).split(".");
  if (!verb) return thing.replace(/_/g, " ");
  const v = verb.replace(/_/g, " ");
  return `${v[0].toUpperCase()}${v.slice(1)} ${thing.replace(/_/g, " ")}`;
};

const actionTone = (code = "") => {
  if (code.endsWith(".delete")) return "admin-badge-danger";
  if (code.endsWith(".create")) return "admin-badge-success";
  if (code.endsWith(".password")) return "admin-badge-gold";
  return "admin-badge-info";
};

const formatDetails = (details) => {
  if (details === null || details === undefined || details === "") return "—";
  if (typeof details === "string") return details;
  if (typeof details === "object") {
    const parts = Object.entries(details).map(([k, v]) => {
      const val = v && typeof v === "object" ? JSON.stringify(v) : String(v);
      return `${k.replace(/_/g, " ")}: ${val}`;
    });
    return parts.length ? parts.join(", ") : "—";
  }
  return String(details);
};

const AuditPanel = () => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ entries: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const req = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/admin/audit", { params: { page, limit: 20 } });
      if (req !== requestRef.current) return;
      setData({
        entries: Array.isArray(res.data?.entries) ? res.data.entries : [],
        total: res.data?.total ?? 0,
        pages: res.data?.pages || 1,
      });
      setLoaded(true);
    } catch (err) {
      if (req === requestRef.current) setError(errorText(err, "Couldn't load the activity log."));
    } finally {
      if (req === requestRef.current) setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="dashboard-section admin-panel">
      <div className="admin-section-head">
        <p className="admin-field-hint">Every change made from this console, newest first.</p>
        <button type="button" className="eg-btn eg-btn-outline admin-btn-sm" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "admin-spin" : ""} /> Refresh
        </button>
      </div>

      {error && (
        <div className="admin-error admin-error-row">
          <span>{error}</span>
          <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
            Try again
          </button>
        </div>
      )}

      <div className={`admin-table-wrap ${loading && loaded ? "is-refreshing" : ""}`} aria-busy={loading}>
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Admin</th>
              <th scope="col">Action</th>
              <th scope="col">Target</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
            {!loaded && loading ? (
              <tr>
                <td colSpan={5} className="admin-muted">Loading activity log…</td>
              </tr>
            ) : data.entries.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState icon={History} title="No admin actions yet">
                    Changes to users, quizzes, announcements and messages will be recorded here.
                  </EmptyState>
                </td>
              </tr>
            ) : (
              data.entries.map((e, i) => (
                <tr key={`${e.at}-${i}`}>
                  <td className="admin-nowrap" title={formatDateTime(e.at)}>
                    <div className="admin-cell-stack">
                      <span>{timeAgo(e.at) || "—"}</span>
                      <small>{formatDateTime(e.at)}</small>
                    </div>
                  </td>
                  <td>{e.admin_name || "—"}</td>
                  <td>
                    <span className={`admin-badge ${actionTone(e.action)}`} title={e.action}>
                      {actionLabel(e.action)}
                    </span>
                  </td>
                  <td className="admin-email">{e.target || "—"}</td>
                  <td className="admin-details">{formatDetails(e.details)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pages={data.pages} total={data.total} noun="entry" plural="entries" onPage={setPage} disabled={loading} />
    </div>
  );
};

export default AuditPanel;
